import { AnyAction } from '@reduxjs/toolkit'
import { get } from 'lodash'

import { FETCH_CONTEXT_ORGANIZATION_SUCCESS, FETCH_CONTEXT_USER_SUCCESS } from '@/components/App/actionTypes'
import { Organization, UserRoles } from '@/components/App/reducer'
import { CHANGE_CURRENT_SPACE } from '@/store/actionTypes'
import { setSentryGlobalScope } from '@/utilities/sentryUtils'

type SentryState = {
  spaceId: string
  user: any
  organization: Pick<Organization, 'id' | 'companyCode' | 'spaces'>
}

const initialState: SentryState = {
  spaceId: '',
  user: {},
  organization: {} as Pick<Organization, 'id' | 'companyCode' | 'spaces'>
}

/**
 * Reducer to store sentry data, a duplicate in some cases to the App reducer
 *
 * @param state
 * @param action
 * @returns {object}
 */
export default function sentryReducer(state = initialState, action: AnyAction): SentryState {
  switch (action.type) {
    case FETCH_CONTEXT_USER_SUCCESS: {
      const newState = {
        ...state,
        // set the organization id as this may have returned before the context organization request
        organization: { ...state.organization, id: action.response.organizationId },
        // use the EXISTING organization state to determine the current space so we can detect if the organization context has loaded
        spaceId: determineSpaceId(state.spaceId, state.organization, action.response.roles),
        user: { id: action.response.id, roles: action.response.roles }
      }
      setSentryGlobalScope(newState.user, newState.organization, newState.spaceId)
      return newState
    }
    case FETCH_CONTEXT_ORGANIZATION_SUCCESS: {
      const newState = {
        ...state,
        organization: {
          companyCode: action.response.companyCode,
          id: action.response.id,
          spaces: action.response.spaces
        },
        spaceId: determineSpaceId(state.spaceId, action.response, state.user.roles)
      }
      setSentryGlobalScope(newState.user, newState.organization, newState.spaceId)
      return newState
    }
    case CHANGE_CURRENT_SPACE: {
      setSentryGlobalScope(state.user, state.organization, action.spaceId)
      return { ...state, spaceId: action.spaceId }
    }
    default: {
      return state
    }
  }
}

const determineSpaceId = (
  selectedSpaceId: string,
  organization: Pick<Organization, 'id' | 'spaces'>,
  roles: UserRoles
) => {
  const hasRoleIn = (spaceId: string) => get(roles, ['spaces', spaceId], []).length > 0
  const getDefaultSpace = () => {
    let defaultSpace = organization.spaces.find(space => space.default && hasRoleIn(space.id))

    if (!defaultSpace) {
      // user doesn't have access to the default space so we simply pick the first one they do have access to
      defaultSpace = organization.spaces.find(({ id }) => hasRoleIn(id))
    }

    // handle the case of a user not having access to ANY spaces
    return defaultSpace ? defaultSpace.id : null
  }

  /*
   * Both the user and organization context requests have to have completed to determine the current space. However, the
   * current space is defaulted when the URL includes a space identifier so we must save this identifier temporarily
   * while the context information is loading.
   */
  if (!organization.id || !roles) return selectedSpaceId

  // a space may not already be selected if it can't be inferred from the URL
  return selectedSpaceId && hasRoleIn(selectedSpaceId) ? selectedSpaceId : getDefaultSpace()
}
